import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Shared vocabulary for a live (in-progress) match.
 *
 * v1 has no live score feed, so we never render a scoreboard during play —
 * showing one (even with em-dashes) reads as "we have live data" when we don't.
 * Instead every surface says the same honest thing: the match is in progress,
 * and the final score lands at full time (match:liveInProgress / liveFinalNote).
 * See SST-4.
 */
const LIVE_RED = '#d1393a';

/** Pulsing dot — the universal "happening now" marker. Red on light surfaces,
 * white when it sits inside a solid red chip. */
export function LivePulseDot({
  size = 6,
  color = LIVE_RED,
}: {
  size?: number;
  color?: string;
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: size / 2,
        background: color,
        flexShrink: 0,
        animation: 'nynjwc-pulse 1.5s infinite',
      }}
    />
  );
}

/**
 * The "● LIVE" ticker chip. `solid` = white text + dot on red (in-row badges);
 * `bare` = red text + dot on transparent (hero header). No minute — v1 has no
 * live clock, and a fake "0'" implies kickoff just happened.
 */
export function LiveChip({
  variant = 'solid',
  style,
}: {
  variant?: 'solid' | 'bare';
  style?: CSSProperties;
}) {
  const { t } = useTranslation();
  const solid = variant === 'solid';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        flexShrink: 0,
        fontWeight: 800,
        letterSpacing: 0.5,
        color: solid ? '#fff' : LIVE_RED,
        ...(solid
          ? { background: LIVE_RED, padding: '6px 10px', borderRadius: 8, fontSize: 12 }
          : { fontSize: 11, letterSpacing: 0.4 }),
        ...style,
      }}
    >
      <LivePulseDot color={solid ? '#fff' : LIVE_RED} />
      {t('common:live')}
    </span>
  );
}

/**
 * The in-progress block that replaces the score in roomy contexts (Home hero,
 * Match Detail): the label plus the "score after the match" note.
 */
export function LiveProgressBlock({ align = 'center' }: { align?: 'center' | 'left' }) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: align === 'center' ? 'center' : 'flex-start',
        gap: 2,
        textAlign: align,
      }}
    >
      <span style={{ fontSize: 15, fontWeight: 800, color: '#0a0a0a', letterSpacing: -0.2 }}>
        {t('match:liveInProgress')}
      </span>
      <span style={{ fontSize: 11, fontWeight: 500, color: '#9a9590' }}>
        {t('match:liveFinalNote')}
      </span>
    </div>
  );
}
